/** Rounded, camera-facing arcade impacts. Metres and domain seconds only; three
 * fixed GPU batches replace per-hit spikes, with no timers or random simulation. */
export function createSkyImpactArt(T, scene, own, reduced) {
  const geometry = own(new T.PlaneGeometry(1, 1));
  function texture(kind) {
    const size = 64,
      data = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) {
        const u = (x + 0.5 - size / 2) / (size / 2),
          v = (y + 0.5 - size / 2) / (size / 2);
        const r = Math.hypot(u, v),
          angle = Math.atan2(v, u);
        const edge =
          kind === 'cloud'
            ? r * (1 + 0.09 * Math.sin(angle * 7) + 0.055 * Math.cos(angle * 11))
            : r;
        const alpha =
          kind === 'ring'
            ? Math.exp(-(((r - 0.72) / 0.085) ** 2))
            : Math.max(0, 1 - edge) ** (kind === 'cloud' ? 1.2 : 2.5);
        const i = (y * size + x) * 4;
        data[i] = data[i + 1] = data[i + 2] = 255;
        data[i + 3] = Math.round(alpha * 255);
      }
    const map = own(new T.DataTexture(data, size, size));
    map.needsUpdate = true;
    return map;
  }
  const cloudMap = texture('cloud'),
    glowMap = texture('glow'),
    ringMap = texture('ring');
  function batch(name, capacity, map, smoke = false) {
    const material = own(
      new T.MeshBasicMaterial({
        map,
        transparent: true,
        depthWrite: false,
        blending: smoke ? T.NormalBlending : T.AdditiveBlending,
        toneMapped: false,
        opacity: smoke ? 0.42 : 1,
      }),
    );
    const mesh = new T.InstancedMesh(geometry, material, capacity);
    mesh.name = name;
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);
    scene.add(mesh);
    return mesh;
  }
  const bloom = batch('Sky rounded impact blooms', 720, cloudMap);
  const pressure = batch('Sky impact pressure halos', 48, ringMap);
  const smoke = batch('Sky impact smoke clouds', 120, cloudMap, true);
  const pose = new T.Object3D(),
    tint = new T.Color(),
    rotation = new T.Quaternion();
  const axis = new T.Vector3(0, 0, 1),
    view = new T.Vector3();
  function screenSpan(camera, point) {
    if (!camera) return Infinity;
    view.set(point.x, point.y, point.z).applyMatrix4(camera.matrixWorldInverse);
    return (
      2 *
      Math.max(camera.near, -view.z) *
      Math.tan((camera.fov * Math.PI) / 360) *
      Math.min(1, camera.aspect)
    );
  }
  const colors = {
    critical: '#ffc34e',
    armor: '#ac9bff',
    body: '#67eaff',
    kill: '#ff853f',
    damage: '#ff526a',
    ring: '#7cffe0',
  };
  function stamp(mesh, x, y, z, size, color, intensity = 1, angle = 0, squash = 1) {
    if (mesh.count >= mesh.instanceMatrix.count || size <= 0 || intensity <= 0) return;
    pose.position.set(x, y, z);
    pose.quaternion.copy(rotation);
    pose.rotateOnAxis(axis, angle);
    pose.scale.set(size, size * squash, 1);
    pose.updateMatrix();
    mesh.setMatrixAt(mesh.count, pose.matrix);
    tint.set(color).multiplyScalar(intensity);
    mesh.setColorAt(mesh.count++, tint);
  }
  const contact = new T.Group();
  contact.name = 'Sky actual shot contact';
  scene.add(contact);
  const contactMaterial = own(
    new T.SpriteMaterial({
      map: glowMap,
      transparent: true,
      depthWrite: false,
      blending: T.AdditiveBlending,
      toneMapped: false,
    }),
  );
  const contactCore = new T.Sprite(contactMaterial);
  contact.add(contactCore);
  function draw(state, camera) {
    if (camera) camera.getWorldQuaternion(rotation);
    else rotation.identity();
    for (const mesh of [bloom, pressure, smoke]) mesh.count = 0;
    for (const item of (state.bursts ?? []).slice(-24)) {
      const age = Math.max(0, (item.duration ?? 0.6) - item.life),
        kill = item.effect === 'kill';
      const duration = kill ? 1.05 : item.effect === 'hit' ? 0.34 : 0.6;
      const p = age / duration;
      if (p >= 1 || item.life <= 0) continue;
      const color =
        colors[item.effect === 'hit' ? (item.quality ?? 'body') : item.effect] ?? colors.kill;
      const baseSize = kill ? 1.25 : item.quality === 'critical' ? 0.65 : 0.4;
      // Near-face shots must reveal the weak point, not blanket it in white.
      // This only bounds cosmetic size; contact and damage remain in metres.
      const proximity = Math.min(
        1,
        (screenSpan(camera, item) * (kill ? 0.36 : 0.2)) / (baseSize * 2),
      );
      const size = baseSize * proximity;
      if (reduced()) {
        stamp(bloom, item.x, item.y, item.z, size, color, 0.65 * (1 - p));
        continue;
      }
      const flash = Math.max(0, 1 - age / 0.14);
      stamp(bloom, item.x, item.y, item.z, size * (1.4 + p), color, flash * 2);
      stamp(bloom, item.x, item.y, item.z + 0.02, size * 0.5, '#fff7d9', flash * 2.5);
      if (age < 0.35)
        stamp(
          pressure,
          item.x,
          item.y,
          item.z,
          size * (0.7 + age * 7),
          color,
          (1 - age / 0.35) * 0.7,
        );
      const count = kill ? 20 : 8;
      for (let i = 0; i < count; i++) {
        const a = i * 2.399963 + (item.id ?? 0) * 0.7,
          speed = kill ? 1.3 + (i % 5) * 0.4 : 0.65 + (i % 3) * 0.2;
        const travel = age * speed * (1 - p * 0.35) * proximity,
          fade = (1 - p) ** 1.6;
        stamp(
          bloom,
          item.x + Math.cos(a) * travel,
          item.y + Math.sin(a) * travel - (kill ? age * age * 0.6 : 0),
          item.z + Math.sin(a * 1.7) * travel * 0.5,
          size * (0.36 + (i % 3) * 0.12) * (1 - p * 0.55),
          i % 4 ? color : '#fff0bd',
          fade * (i % 4 ? 1.1 : 1.7),
          a,
          0.85,
        );
      }
      if (kill)
        for (let i = 0; i < 5; i++) {
          const a = i * 2.399 + item.id;
          stamp(
            smoke,
            item.x + Math.cos(a) * age * proximity,
            item.y + (age * 0.7 + Math.sin(a) * age * 0.5) * proximity,
            item.z - 0.15,
            (0.55 + age * 2.5) * (1 - p ** 3) * proximity,
            '#4b4560',
            1,
            a,
          );
        }
    }
    const impact = state.lastImpact,
      age = impact ? state.time - impact.time : Infinity;
    contact.visible = age >= 0 && age < 0.22;
    if (contact.visible) {
      contact.position.set(impact.x, impact.y, impact.z + 0.05);
      contactMaterial.color.set(colors[impact.quality] ?? colors.body);
      contactMaterial.opacity = (1 - age / 0.22) * 0.9;
      contactCore.scale.setScalar(
        Math.min(
          (impact.quality === 'critical' ? 0.9 : 0.55) * (reduced() ? 1 : 1 + age),
          screenSpan(camera, impact) * 0.12,
        ),
      );
    }
    for (const mesh of [bloom, pressure, smoke]) {
      mesh.visible = mesh.count > 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }
  return {
    draw,
    dispose() {
      for (const mesh of [bloom, pressure, smoke]) {
        scene.remove(mesh);
        mesh.dispose();
      }
      scene.remove(contact);
    },
  };
}
